import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface Price {
  type: string;
  plan_id: number;
  name: string;
  plan_prc: number;
  is_discount: string;
}
interface SpecialItem {
  img: string;
  text: string;
}

interface MembershipCardProps {
  title: string;
  description: string;
  special: SpecialItem[];
  prices?: Price[];
  selected?: number;
  setSelected?: (idx: number) => void;
  isPremium?: boolean;
}

const primium = {
  type: 'P',
  plan_id: 10,
  name: '전국 12개월 프리미엄 멤버십 얼리버드',
  plan_prc: 1500000,
  is_discount: 'Y',
};

const MembershipCard: React.FC<MembershipCardProps> = ({
  title,
  description,
  special,
  prices,
  selected = 3,
  setSelected,
  isPremium = false,
}) => {
  const navigate = useNavigate();

  const naver_script = (selectedPlan: Price) => {
    if (window.wcs) {
      if (!window.wcs_add as any) window.wcs_add = {};
      window.wcs_add['wa'] = 's_2489d344048e';
      var _conv: any = {};
      _conv.type = 'begin_checkout'; //(필수)
      _conv.items = [
        {
          id: selectedPlan.plan_id,
          name: selectedPlan.name,
          payAmount: selectedPlan.plan_prc,
        },
      ];
      window.wcs.trans(_conv);
    }
  };

  return (
    <CardContainer>
      <TitleBox>
        <Title>
          <Badge
            color1={isPremium ? '#008aff' : '#00a2e7'}
            color2={isPremium ? '#6900f3' : '#94bf08'}
            bcolor1={isPremium ? '#3F9BFF' : '#3CB5DE'}
            bcolor2={isPremium ? '#7C29FB' : '#AAD545'}
          >
            <span>{title}</span>
          </Badge>
          <TitleText>{title} 멤버십</TitleText>
        </Title>
        <Description dangerouslySetInnerHTML={{ __html: description }} />
      </TitleBox>

      <Divider />

      <SpecialList>
        {special.map((item, index) => (
          <SpecialRow key={index}>
            <img src={item.img} alt="" width={20} height={20} />
            <span dangerouslySetInnerHTML={{ __html: item.text }} />
          </SpecialRow>
        ))}
      </SpecialList>

      {prices && setSelected && (
        <MonthBox>
          <span>기간 선택</span>
          <MonthOptions>
            {['1개월', '3개월', '6개월', '12개월'].map((label, idx) => (
              <PlanSelectButton
                key={label}
                label={label}
                selected={selected === idx}
                onClick={() => setSelected(idx)}
              />
            ))}
          </MonthOptions>
        </MonthBox>
      )}

      {isPremium && (
        <PrimiumDescriptionContainer>
          <PrimiumDescripton>
            <img
              src="/assets/images/check/check-red.svg"
              alt=""
              width={20}
              height={20}
            />
            <span>프리미엄 멤버십 한정 혜택</span>
          </PrimiumDescripton>
          <PrimiumDescriptionBox>
            <span>
              VIP 퀀트 투자 보고서 <strong>무제한</strong> 제공
            </span>
          </PrimiumDescriptionBox>
        </PrimiumDescriptionContainer>
      )}

      <PriceBox>
        <PriceBeforeContainer>
          <OriginalPrice>
            {prices
              ? `${(prices[selected].plan_prc * 2).toLocaleString('ko-KR')}원`
              : '2,990,000원'}
          </OriginalPrice>
          <Tooltip color={isPremium ? '#6309f4' : '#09a3db'}>
            <span>구매 시 50% 즉시 할인</span>
          </Tooltip>
        </PriceBeforeContainer>
        <DiscountPrice>
          {prices
            ? `${prices[selected].plan_prc.toLocaleString('ko-KR')}원`
            : '1,500,000원'}
          <span>/1년</span>
        </DiscountPrice>
      </PriceBox>

      <PayButton
        onClick={() => {
          const selectedPlan = prices ? prices[selected] : primium;
          naver_script(selectedPlan);
          navigate('/payment/method', { state: { selectedPlan } });
        }}
        color={isPremium ? '#6309f4' : '#09a3db'}
      >
        50% 할인받고 결제하러 가기
      </PayButton>
    </CardContainer>
  );
};

export default MembershipCard;

const CardContainer = styled.div`
  width: 100%;
  padding: 24px 20px;
  border-radius: 24px;
  border: 2px solid #e5e8ea;
  background-color: #ffffff;
  box-shadow: 0px 4px 30px 0px #8d95a340;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;

const Badge = styled.div<{
  color1: string;
  color2: string;
  bcolor1: string;
  bcolor2: string;
}>`
  border-radius: 4px;
  padding: 2px 16px;
  background: ${({ color1, color2 }) =>
    `linear-gradient(105.88deg, ${color1} 0%, ${color2} 100%)`};
  border: 2px solid
    ${({ bcolor1, bcolor2 }) =>
      `linear-gradient(94.45deg, ${bcolor1} 0%, ${bcolor2} 100%)`};
  span {
    font-family: Paperlogy;
    font-weight: 700;
    font-size: 14px;
    color: #ffffff;
  }
`;

const TitleText = styled.span`
  font-weight: 700;
  font-size: 28px;
  line-height: 145%;
  color: #222222;
`;

const Description = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  text-align: center;
  color: #616161;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e5e8ea;
`;

const SpecialList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SpecialRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  span {
    font-size: 14px;
    font-weight: 500;
    color: #222222;
  }
`;

const MonthBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  span {
    font-size: 13px;
    font-weight: 700;
    color: '#222222';
  }
`;

const MonthOptions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const PriceBox = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  width: 100%;
`;

const PriceBeforeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const OriginalPrice = styled.span`
  text-decoration: line-through;
  color: #616161;
  font-size: 14px;
`;

const Tooltip = styled.div<{ color: string }>`
  padding: 8px 14px;
  border-radius: 999px;
  background-color: ${({ color }) => color};
  position: relative;
  animation: blink 1s infinite;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 16%;
    width: 0;
    height: 0;
    border: 8px solid transparent;
    border-top-color: ${({ color }) => color};
    border-bottom: 0;
    margin-left: -8px;
    margin-bottom: -8px;
  }
  span {
    color: #eff0ff;
    font-size: 14px;
  }
  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
`;

const DiscountPrice = styled.div`
  display: flex;
  gap: 4px;
  align-items: end;
  font-weight: 700;
  font-size: 32px;
  span {
    font-weight: 500;
    font-size: 14px;
    color: #37383c9c;
  }
`;

const PayButton = styled.button<{ color: string }>`
  border-radius: 8px;
  padding: 20px 28px;
  background-color: ${({ color }) => color};
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
  border: none;
`;

const PrimiumDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 8px;
`;
const PrimiumDescripton = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  span {
    font-weight: 700;
    font-size: 13px;
    line-height: 145%;
    color: #222222;
  }
`;

const PrimiumDescriptionBox = styled.div`
  display: flex;
  border-radius: 8px;
  padding-top: 19px;
  padding-bottom: 19px;
  justify-content: center;
  background-color: #ebeef6;
  span {
    text-align: center;
    font-weight: 700;
    font-size: 16px;
    color: #6309f4;
  }
`;

const PlanSelectButton = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => {
  return (
    <PlanButtonWrapper selected={selected} onClick={onClick}>
      <PlanRadioCircle selected={selected} />
      <PlanLabel>{label}</PlanLabel>
    </PlanButtonWrapper>
  );
};

const PlanButtonWrapper = styled.div<{ selected: boolean }>`
  width: 25%;
  height: 64px;
  border-radius: 12px;
  border: 2px solid ${({ selected }) => (selected ? '#2323FF' : '#E5E8EA')};
  background-color: ${({ selected }) => (selected ? '#EFF0FF' : '#FFFFFF')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const PlanRadioCircle = styled.div<{ selected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid rgba(112, 115, 124, 0.16);
  background-color: ${({ selected }) =>
    selected ? 'rgba(112, 115, 124, 0.16)' : '#ffffff'};
  position: relative;
  margin-bottom: 6px;
  &::after {
    content: '';
    display: ${({ selected }) => (selected ? 'block' : 'none')};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #3c5afe;
  }
`;

const PlanLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #616161;
`;
